import * as Sentry from '@sentry/react';
import superjson from 'superjson';

import { getClientApiUrl } from '@/config/getEnv';
import { redirectToLoginOn401 } from '@/library/api/redirectToLoginOn401';

export type ProxyParams = {
  entity: string;
  method: string;
  args: unknown[];
};

// Call sites catch the thrown error and show a toast, so it never becomes an uncaught error that
// Sentry would see. Report it here, centrally, for every action call. 401/403 are expected
// auth/permission denials and are skipped to avoid noise.
const reportProxyFailure = (entity: string, method: string, status: number, body: string): void => {
  if (status === 401 || status === 403) return;
  Sentry.captureException(new Error(`Proxy request failed: ${body}`), {
    tags: { entity, method, proxyStatus: status },
  });
};

const getBaseUrl = (): string => {
  return getClientApiUrl();
};

const hasFormDataDeep = (value: unknown): boolean => {
  if (value instanceof FormData) return true;
  if (Array.isArray(value)) return value.some(hasFormDataDeep);
  if (value !== null && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some(hasFormDataDeep);
  }
  return false;
};

const extractFormData = (value: unknown, list: FormData[]): unknown => {
  if (value instanceof FormData) {
    const index = list.length;
    list.push(value);
    return { __formDataRef: index };
  }
  if (Array.isArray(value)) return value.map((v) => extractFormData(v, list));
  if (value !== null && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      result[k] = extractFormData(v, list);
    }
    return result;
  }
  return value;
};

/**
 * On non-2xx, log the real server body (parsed JSON when possible, raw text
 * otherwise) to the browser console along with status, URL, and which
 * entity.method was called — then throw with the raw text so existing
 * error-handling stays compatible.
 */
const handleProxyError = async (response: Response, entity: string, method: string): Promise<never> => {
  redirectToLoginOn401(response);
  const text = await response.text();
  let body: unknown = text;
  try {
    body = JSON.parse(text);
  } catch {
    /* leave as text */
  }
  console.error(`[Action ${response.status}] ${entity}.${method} → ${response.url}`, body);
  reportProxyFailure(entity, method, response.status, text);
  throw new Error(`Proxy request failed: ${text}`);
};

export const proxy = async <T = unknown>({ entity, method, args }: ProxyParams): Promise<T> => {
  const baseUrl = getBaseUrl();

  if (args.some(hasFormDataDeep)) {
    const formDataList: FormData[] = [];
    const modifiedArgs = args.map((arg) => extractFormData(arg, formDataList));

    const formData = new FormData();
    formData.append('entity', entity);
    formData.append('method', method);
    formData.append('args', superjson.stringify(modifiedArgs));

    for (let i = 0; i < formDataList.length; i++) {
      for (const [key, value] of formDataList[i].entries()) {
        formData.append(`formData_${i}_${key}`, value);
      }
    }

    const response = await fetch(`${baseUrl}/actions/form`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) await handleProxyError(response, entity, method);

    const text = await response.text();
    return superjson.parse<T>(text);
  }

  const response = await fetch(`${baseUrl}/actions/json`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: superjson.stringify({ entity, method, args }),
  });

  if (!response.ok) await handleProxyError(response, entity, method);

  const text = await response.text();
  return superjson.parse<T>(text);
};
